/**
 * User-activity tracker — module-singleton event buffer.
 *
 * Ports the internal `ActivityTracker` from the legacy
 * `frontend/static/js/core/analytics.js`. It records four custom event
 * types (`page_view`, `document_open`, `link_out`, `timeseries_query`) and
 * POSTs them to `/api/journal` (a deliberately tracker-vocabulary-free path —
 * see `endpoints.ts::activityEndpoints`), which writes `fed_data.user_activity`.
 *
 * The legacy GA4 / gtag integration is intentionally NOT ported — only the
 * internal server-side tracking is restored.
 *
 * Buffering / flush policy (matches the legacy tracker):
 *   - Flush every 10s on a timer.
 *   - Flush immediately once 20 events are buffered.
 *   - Flush on `beforeunload` and on `visibilitychange` → hidden, using
 *     `navigator.sendBeacon` so the request survives page teardown.
 *
 * Auth gating: events are only sent for an authenticated user. The buffer
 * is a non-React module, so it cannot call `useAuth()`; instead the app
 * registers the shared TanStack Query client via {@link registerAuthSource}
 * and the tracker reads the cached `/auth/status` response from it.
 *
 * Wire format — POST body is `{ events: [...] }`. Each event object is:
 *   {
 *     event_name:      string,   // one of the 4 tracked types
 *     event_category:  '',       // reserved; backend column, always empty
 *     page_path:       string,   // window.location.pathname at record time
 *     description:     string,   // human-readable, truncated to 200 chars
 *     ...eventData,              // extra fields → backend `event_data` JSON
 *   }
 * The backend (`AdminAnalyticsData.log_user_activity_batch`) promotes
 * `event_name` / `event_category` / `page_path` / `description` to columns
 * and folds every other key into the `event_data` JSONB column.
 */
import type { QueryClient } from '@tanstack/react-query';
import { AUTH_QUERY_KEY } from '@/lib/auth/useAuth';
import { endpoints } from '@/lib/api/endpoints';
import { apiPost } from '@/lib/api/client';

/** Flush the buffer on this cadence (ms). */
const FLUSH_INTERVAL_MS = 10_000;
/** Flush immediately once the buffer reaches this size. */
const FLUSH_MAX_SIZE = 20;
/** `description` column is capped at 200 chars in the backend. */
const DESCRIPTION_MAX_LEN = 200;

/** Extra per-event fields — folded into the backend `event_data` JSON column. */
export type EventData = Record<string, unknown>;

/** One queued event, in the exact shape the backend expects. */
interface ActivityEvent {
  event_name: string;
  event_category: string;
  page_path: string;
  description: string;
  [key: string]: unknown;
}

/**
 * Minimal shape the tracker reads from the cached `/auth/status` response.
 * The full raw type is internal to `useAuth`; the tracker only needs to
 * know whether there is an authenticated user.
 */
interface CachedAuthStatus {
  authenticated?: boolean;
  user?: { email?: string | null };
}

/** Buffered events not yet flushed. */
let buffer: ActivityEvent[] = [];
/** Interval handle for the periodic flush; `null` until the timer starts. */
let flushTimer: ReturnType<typeof setInterval> | null = null;
/** `true` once lifecycle listeners + the flush timer have been installed. */
let started = false;
/** Shared query client, registered by the app so we can read auth state. */
let queryClient: QueryClient | null = null;

/**
 * Register the app's TanStack Query client so the tracker can read the
 * cached auth status. Called once from `App.tsx` (inside the provider tree).
 */
export function registerAuthSource(client: QueryClient): void {
  queryClient = client;
}

/** Read the authenticated user's email from the auth cache, or `null`. */
function currentUserEmail(): string | null {
  if (!queryClient) return null;
  const status = queryClient.getQueryData<CachedAuthStatus>(AUTH_QUERY_KEY);
  if (!status?.authenticated) return null;
  const email = status.user?.email;
  return email ? email : null;
}

/** Whether a signed-in user is present — events are sent only when `true`. */
function isAuthenticated(): boolean {
  return currentUserEmail() !== null;
}

/**
 * POST the buffered events. On the unload path we use `navigator.sendBeacon`
 * (the request must survive page teardown); otherwise the normal API client.
 */
function send(events: ActivityEvent[], useBeacon: boolean): void {
  const payload = JSON.stringify({ events });
  const url = endpoints.activity.track();

  if (useBeacon && typeof navigator !== 'undefined' && typeof navigator.sendBeacon === 'function') {
    const blob = new Blob([payload], { type: 'application/json' });
    navigator.sendBeacon(url, blob);
    return;
  }

  // Fire-and-forget — tracking failures must never surface to the user.
  // `keepalive` lets the request outlive a navigation when sendBeacon is
  // unavailable (the visibilitychange path can still race a teardown).
  // `redirectOn401: false` keeps this background flush from yanking the user
  // to /login on an expired session — the visible re-login must come from a
  // real data query or the auth-status revalidation, never from analytics.
  void apiPost(url, { events }, { keepalive: true, redirectOn401: false }).catch(() => {});
}

/**
 * Flush the buffer.
 *
 * @param useBeacon - send via `navigator.sendBeacon` (unload path) when true.
 */
export function flush(useBeacon = false): void {
  if (buffer.length === 0) return;
  const events = buffer;
  buffer = [];
  send(events, useBeacon);
}

/** Install the flush timer + page-lifecycle listeners exactly once. */
function ensureStarted(): void {
  if (started || typeof window === 'undefined') return;
  started = true;

  flushTimer = setInterval(() => flush(false), FLUSH_INTERVAL_MS);

  // Unload path: a synchronous beacon flush so events aren't lost.
  window.addEventListener('beforeunload', () => flush(true));
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') flush(true);
  });
}

/**
 * Record a user-activity event.
 *
 * No-ops for anonymous users (no authenticated session). The first call by
 * a signed-in user installs the flush timer + lifecycle listeners.
 *
 * @param eventName   - event type (`page_view`, `document_open`, ...).
 * @param description - human-readable summary; truncated to 200 chars.
 * @param eventData   - optional extra fields folded into `event_data` JSON.
 */
export function trackActivity(
  eventName: string,
  description: string,
  eventData: EventData = {},
): void {
  if (!eventName || !isAuthenticated()) return;

  ensureStarted();

  buffer.push({
    event_name: eventName,
    // Reserved column — the legacy tracker always sent it empty.
    event_category: '',
    page_path: typeof window !== 'undefined' ? window.location.pathname : '',
    description: (description || '').slice(0, DESCRIPTION_MAX_LEN),
    ...eventData,
  });

  if (buffer.length >= FLUSH_MAX_SIZE) {
    flush(false);
  }
}

/**
 * Test-only reset — clears the buffer, stops the timer, and forgets the
 * registered auth source so each test starts from a clean tracker.
 */
export function __resetTrackerForTests(): void {
  buffer = [];
  if (flushTimer !== null) {
    clearInterval(flushTimer);
    flushTimer = null;
  }
  started = false;
  queryClient = null;
}

/** Test-only constants so the unit test asserts against the real values. */
export const __trackerConfig = {
  FLUSH_INTERVAL_MS,
  FLUSH_MAX_SIZE,
  DESCRIPTION_MAX_LEN,
} as const;
